import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axiosInstance from "../components/api/axiosInstance";
import PortalLocationSelects from "./PortalLocationSelects";
import { PROFILE_EDITABLE, PROFILE_ADDRESS_EDITABLE } from "./customerProfileFields";
import { mobileValidationMessage, sanitizeMobileInput } from "./portalPhone";

function buildFormFromProfile(data) {
  const initial = {};
  PROFILE_EDITABLE.forEach(([key]) => {
    initial[key] =
      key === "Alt_Phone" ? sanitizeMobileInput(data[key] || "") : data[key] || "";
  });
  PROFILE_ADDRESS_EDITABLE.forEach(([key]) => {
    initial[key] = data[key] || "";
  });
  initial.Zip_Code = data.Zip_Code || "";
  initial.countryId = data.Country_Id ? String(data.Country_Id) : "";
  initial.stateId = data.State_Id ? String(data.State_Id) : "";
  initial.cityId = data.City_Id ? String(data.City_Id) : "";
  return initial;
}

function buildProfilePayload(form) {
  const payload = {};
  PROFILE_EDITABLE.forEach(([key]) => {
    payload[key] = typeof form[key] === "string" ? form[key].trim() || null : form[key];
  });
  PROFILE_ADDRESS_EDITABLE.forEach(([key]) => {
    payload[key] = typeof form[key] === "string" ? form[key].trim() || null : form[key];
  });
  payload.Zip_Code = typeof form.Zip_Code === "string" ? form.Zip_Code.trim() || null : form.Zip_Code;
  payload.Country_Id = form.countryId ? Number(form.countryId) : null;
  payload.State_Id = form.stateId ? Number(form.stateId) : null;
  payload.City_Id = form.cityId ? Number(form.cityId) : null;
  return payload;
}

function CustomerProfileEdit() {
  const navigate = useNavigate();
  const [profile, setProfile] = useState(null);
  const [form, setForm] = useState({});
  const [loading, setLoading] = useState(true);
  const [savingProfile, setSavingProfile] = useState(false);
  const [profileMsg, setProfileMsg] = useState({ type: "", text: "" });

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const res = await axiosInstance.get("/client/me");
        if (!active) return;
        const data = res.data?.data || {};
        setProfile(data);
        setForm(buildFormFromProfile(data));
      } catch (_) {
        if (active) setProfileMsg({ type: "error", text: "Could not load your profile." });
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => {
      active = false;
    };
  }, []);

  const setField = (name, value) => setForm((f) => ({ ...f, [name]: value }));

  const saveProfile = async (e) => {
    e.preventDefault();
    setProfileMsg({ type: "", text: "" });

    const altPhoneError = mobileValidationMessage(form.Alt_Phone, { label: "Alternate phone" });
    if (altPhoneError) {
      setProfileMsg({ type: "error", text: altPhoneError });
      return;
    }

    setSavingProfile(true);
    try {
      const res = await axiosInstance.put("/client/me", buildProfilePayload(form));
      const data = res.data?.data;
      if (data) {
        setProfile(data);
        try {
          localStorage.setItem("client", JSON.stringify(data));
        } catch (_) {}
      }
      setProfileMsg({ type: "success", text: "Profile updated." });
      setTimeout(() => navigate("/portal/profile"), 500);
    } catch (err) {
      setProfileMsg({ type: "error", text: err.response?.data?.message || "Could not update profile." });
    } finally {
      setSavingProfile(false);
    }
  };

  if (loading) {
    return <p className="cportal-muted">Loading…</p>;
  }

  const isTenantUser = profile?.portal_role === "tenant_user";

  return (
    <form className="cportal-card" onSubmit={saveProfile} noValidate>
      <div className="cportal-card-head">
        <h2>Edit account details</h2>
      </div>
      {profileMsg.text && (
        <div className={`cportal-alert cportal-alert-${profileMsg.type === "error" ? "error" : "success"}`}>
          {profileMsg.text}
        </div>
      )}

      <h3 className="cportal-form-section">Contact</h3>
      <div className="cportal-form-grid cportal-form-grid--3">
        <div className="cportal-field">
          <label>{isTenantUser ? "Company email (login)" : "Email (login)"}</label>
          <input value={(isTenantUser ? profile?.Company_Email : profile?.Email) || ""} disabled />
        </div>
        <div className="cportal-field">
          <label>Phone</label>
          <input value={profile?.Phone || ""} disabled />
        </div>
        {PROFILE_EDITABLE.map(([key, label]) => (
          <div className="cportal-field" key={key}>
            <label>{label}</label>
            {key === "Alt_Phone" ? (
              <input
                value={form[key] || ""}
                onChange={(e) => setField(key, sanitizeMobileInput(e.target.value))}
                inputMode="numeric"
                maxLength={10}
                placeholder="10-digit number"
              />
            ) : (
              <input value={form[key] || ""} onChange={(e) => setField(key, e.target.value)} />
            )}
          </div>
        ))}
      </div>

      {!isTenantUser && (
        <>
          <h3 className="cportal-form-section">Address</h3>
          <div className="cportal-form-grid cportal-form-grid--3">
            {PROFILE_ADDRESS_EDITABLE.map(([key, label]) => (
              <div className="cportal-field" key={key}>
                <label>{label}</label>
                <input value={form[key] || ""} onChange={(e) => setField(key, e.target.value)} />
              </div>
            ))}
            <PortalLocationSelects
              countryId={form.countryId}
              stateId={form.stateId}
              cityId={form.cityId}
              onChange={({ countryId, stateId, cityId }) =>
                setForm((f) => ({ ...f, countryId, stateId, cityId }))
              }
            />
            <div className="cportal-field">
              <label>Zip code</label>
              <input value={form.Zip_Code || ""} onChange={(e) => setField("Zip_Code", e.target.value)} />
            </div>
          </div>
        </>
      )}

      <div className="cportal-form-actions">
        <button
          type="button"
          className="cportal-btn cportal-btn-ghost"
          onClick={() => navigate("/portal/profile")}
          disabled={savingProfile}
        >
          Cancel
        </button>
        <button type="submit" className="cportal-btn cportal-btn-primary" disabled={savingProfile}>
          {savingProfile ? "Saving…" : "Save changes"}
        </button>
      </div>
    </form>
  );
}

export default CustomerProfileEdit;
